import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { EditOutlined, EyeOutlined, PlusOutlined, ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import api from '../../../api/client';
import { usePermissions } from '../../../context/permissionsContext';
import { MODULO_LABELS } from '../../../constants/permissions';

const { Title, Text } = Typography;

const Roles = () => {
  const { hasPermission } = usePermissions();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [incluirInactivos, setIncluirInactivos] = useState(false);
  const [modalDetalleOpen, setModalDetalleOpen] = useState(false);
  const [modalPermisosOpen, setModalPermisosOpen] = useState(false);
  const [modalRolOpen, setModalRolOpen] = useState(false);
  const [rolSeleccionado, setRolSeleccionado] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [permisosLoading, setPermisosLoading] = useState(false);
  const [modulos, setModulos] = useState({});
  const [selectedPermissionIds, setSelectedPermissionIds] = useState(new Set());
  const [savingPermisos, setSavingPermisos] = useState(false);
  const [editingRolId, setEditingRolId] = useState(null);
  const [savingRol, setSavingRol] = useState(false);
  const [formRol] = Form.useForm();

  const loadRoles = async () => {
    setLoading(true);
    try {
      const params = { withCounts: true };
      if (incluirInactivos) params.incluir_inactivos = true;
      const { data } = await api.get('/roles', { params });
      setRoles(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, [incluirInactivos]);

  const handleVerDetalle = async (record) => {
    setRolSeleccionado(null);
    setModalDetalleOpen(true);
    setDetalleLoading(true);
    try {
      const { data } = await api.get(`/roles/${record.rol_id}`);
      setRolSeleccionado(data);
    } catch (e) {
      message.error('Error al cargar el rol');
      setModalDetalleOpen(false);
    } finally {
      setDetalleLoading(false);
    }
  };

  const handleAbrirEditarPermisos = async (record) => {
    setRolSeleccionado(null);
    setModalPermisosOpen(true);
    setPermisosLoading(true);
    setSelectedPermissionIds(new Set());
    try {
      const [roleRes, modulosRes] = await Promise.all([
        api.get(`/roles/${record.rol_id}`),
        api.get('/permisos/modulos'),
      ]);
      setRolSeleccionado(roleRes.data);
      setModulos(modulosRes.data && typeof modulosRes.data === 'object' ? modulosRes.data : {});
      setSelectedPermissionIds(new Set(roleRes.data.permissionIds || []));
    } catch (e) {
      message.error('Error al cargar datos');
      setModalPermisosOpen(false);
    } finally {
      setPermisosLoading(false);
    }
  };

  const handleTogglePermiso = (perId, checked) => {
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(perId);
      else next.delete(perId);
      return next;
    });
  };

  const handleGuardarPermisos = async () => {
    if (!rolSeleccionado) return;
    setSavingPermisos(true);
    try {
      await api.put(`/roles/${rolSeleccionado.rol_id}/permissions`, {
        permission_ids: Array.from(selectedPermissionIds),
      });
      message.success('Permisos actualizados');
      setModalPermisosOpen(false);
      loadRoles();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al guardar permisos');
    } finally {
      setSavingPermisos(false);
    }
  };

  const handleOpenNuevoRol = () => {
    setEditingRolId(null);
    formRol.resetFields();
    formRol.setFieldsValue({ rol_es_admin: false, rol_activo: true, rol_orden: 0 });
    setModalRolOpen(true);
  };

  const handleOpenEditarRol = (record) => {
    setEditingRolId(record.rol_id);
    formRol.setFieldsValue({
      rol_codigo: record.rol_codigo,
      rol_nombre: record.rol_nombre,
      rol_es_admin: record.rol_es_admin,
      rol_activo: record.rol_activo !== false,
      rol_orden: record.rol_orden ?? 0,
    });
    setModalRolOpen(true);
  };

  const handleGuardarRol = async () => {
    try {
      const values = await formRol.validateFields();
      setSavingRol(true);
      if (editingRolId) {
        await api.put(`/roles/${editingRolId}`, values);
        message.success('Rol actualizado');
      } else {
        await api.post('/roles', values);
        message.success('Rol creado. Asignale permisos desde "Editar permisos".');
      }
      setModalRolOpen(false);
      loadRoles();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Error al guardar');
    } finally {
      setSavingRol(false);
    }
  };

  const columns = [
    { title: 'Código', dataIndex: 'rol_codigo', render: (c) => <Text code>{c}</Text> },
    { title: 'Nombre', dataIndex: 'rol_nombre' },
    {
      title: 'Admin',
      dataIndex: 'rol_es_admin',
      width: 80,
      render: (v) => (v ? <Tag color="gold">Si</Tag> : <Tag>No</Tag>),
    },
    {
      title: 'Activo',
      dataIndex: 'rol_activo',
      width: 80,
      render: (v) => (v !== false ? <Tag color="green">Si</Tag> : <Tag>No</Tag>),
    },
    {
      title: 'Permisos',
      key: 'permissionCount',
      width: 100,
      render: (_, r) =>
        r.rol_es_admin ? <Tag color="green">Todos</Tag> : <span>{r.permissionCount ?? 0}</span>,
    },
    {
      title: 'Usuarios',
      dataIndex: 'userCount',
      width: 90,
      render: (c) => (c != null ? c : '—'),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 260,
      render: (_, record) => (
        <Space size={0} wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleVerDetalle(record)}>
            Ver
          </Button>
          {hasPermission('usuarios', 'editar') && (
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenEditarRol(record)}>
              Editar
            </Button>
          )}
          {!record.rol_es_admin && hasPermission('usuarios', 'editar') && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleAbrirEditarPermisos(record)}
            >
              Permisos
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={<Title level={5} style={{ margin: 0, color: '#198039' }}>Roles del sistema</Title>}
      style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
      extra={
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={loadRoles} loading={loading}>
            Actualizar
          </Button>
          {hasPermission('usuarios', 'crear') && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenNuevoRol}>
              Nuevo rol
            </Button>
          )}
        </Space>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Cada rol define qué módulos puede ver y usar cada usuario. Asigná roles desde la pantalla Usuarios.
      </Typography.Paragraph>
      <Checkbox checked={incluirInactivos} onChange={(e) => setIncluirInactivos(e.target.checked)} style={{ marginBottom: 12 }}>
        Incluir roles inactivos
      </Checkbox>
      <Table rowKey="rol_id" columns={columns} dataSource={roles} loading={loading} size="small" pagination={{ pageSize: 10 }} />

      <Modal
        title={rolSeleccionado ? `Rol: ${rolSeleccionado.rol_nombre}` : 'Detalle del rol'}
        open={modalDetalleOpen}
        onCancel={() => setModalDetalleOpen(false)}
        footer={null}
        width={640}
      >
        {detalleLoading && !rolSeleccionado ? (
          <div style={{ padding: 24, textAlign: 'center' }}>Cargando...</div>
        ) : rolSeleccionado ? (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Código"><Text code>{rolSeleccionado.rol_codigo}</Text></Descriptions.Item>
              <Descriptions.Item label="Nombre">{rolSeleccionado.rol_nombre}</Descriptions.Item>
              <Descriptions.Item label="Es admin">
                {rolSeleccionado.rol_es_admin ? <Tag color="gold">Si (todos los permisos)</Tag> : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="Usuarios">{rolSeleccionado.userCount ?? 0}</Descriptions.Item>
            </Descriptions>
            <div style={{ marginTop: 16 }}>
              <Text strong>Permisos</Text>
              {rolSeleccionado.rol_es_admin ? (
                <div style={{ marginTop: 8 }}><Tag color="green">Todos los permisos</Tag></div>
              ) : Object.keys(rolSeleccionado.permissionsByModule || {}).length ? (
                Object.entries(rolSeleccionado.permissionsByModule).map(([modulo, perms]) => (
                  <div key={modulo} style={{ marginTop: 12 }}>
                    <Text style={{ color: '#198039' }}>{MODULO_LABELS[modulo] || modulo}</Text>
                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(perms || []).map((p) => (
                        <Tag key={p.per_codigo}>{p.per_descripcion || p.per_codigo}</Tag>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ marginTop: 8 }}><Text type="secondary">Sin permisos asignados</Text></div>
              )}
            </div>
          </>
        ) : null}
      </Modal>

      <Modal
        title={rolSeleccionado ? `Permisos — ${rolSeleccionado.rol_nombre}` : 'Editar permisos'}
        open={modalPermisosOpen}
        onCancel={() => setModalPermisosOpen(false)}
        onOk={() => void handleGuardarPermisos()}
        okText="Guardar"
        confirmLoading={savingPermisos}
        width={560}
      >
        {permisosLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>Cargando...</div>
        ) : (
          <div style={{ maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {Object.entries(modulos).map(([modulo, perms]) => (
              <div key={modulo}>
                <Text strong style={{ color: '#198039', display: 'block', marginBottom: 8 }}>
                  {MODULO_LABELS[modulo] || modulo}
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {(perms || []).map((p) => (
                    <Checkbox
                      key={p.per_id}
                      checked={selectedPermissionIds.has(p.per_id)}
                      onChange={(e) => handleTogglePermiso(p.per_id, e.target.checked)}
                    >
                      {p.per_descripcion || p.per_codigo}
                    </Checkbox>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        title={editingRolId ? 'Editar rol' : 'Nuevo rol'}
        open={modalRolOpen}
        onCancel={() => setModalRolOpen(false)}
        onOk={() => void handleGuardarRol()}
        okText="Guardar"
        confirmLoading={savingRol}
      >
        <Form form={formRol} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="rol_codigo" label="Código" rules={[{ required: true, message: 'Requerido' }]}>
            <Input placeholder="ej: operador_ventas" disabled={!!editingRolId} />
          </Form.Item>
          <Form.Item name="rol_nombre" label="Nombre" rules={[{ required: true, message: 'Requerido' }]}>
            <Input placeholder="Nombre visible del rol" />
          </Form.Item>
          <Form.Item name="rol_es_admin" label="Administrador (todos los permisos)" valuePropName="checked">
            <Switch disabled={!!editingRolId} />
          </Form.Item>
          {editingRolId && (
            <Form.Item name="rol_activo" label="Activo" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
          <Form.Item name="rol_orden" label="Orden">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Roles;
